import { 
  faPlus, 
  faFolder, 
  faInbox, 
  faPaperPlane, 
  faTrash, 
  faExclamationTriangle, 
  faArchive, 
  faEdit,
  faCaretDown,
  faCaretRight,
  faSync,
  faEllipsisV
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import UilTimes from '@iconscout/react-unicons/icons/uil-times';
import classNames from 'classnames';
import Button from '../../../components/base/Button';
import {
  SidebarItem,
  filteredItems,
  labelItems
} from '../../../data/email';
import React, { useState, useEffect, useRef } from 'react';
import { Nav, Spinner, Modal, Form, Dropdown } from 'react-bootstrap';
import { Link, useParams, useLocation } from 'react-router-dom';

// Importar los componentes de configuración
import EmailSettingsModal, { EmailConfig } from './EmailSettingsModal';
import EmailSettingsButton from './EmailSettingsButton';
import { createFolder, deleteFolder, renameFolder, validateFolderName, isSystemFolder } from '../../../service/folderManagementService';

// 🔧 CONFIGURACIÓN POR DEFECTO
const DEFAULT_EMAIL_CONFIG = {
  server: 'mail.linuxcorp.cl',
  username: 'anubis@linuxcorp.cl',
  password: '4n00bies.$_240829',
  imapPort: 993,
  smtpPort: 465,
  ssl: true,
  displayName: 'Anubis AI'
};

// Interface para carpetas del servidor
interface ServerFolder {
  name: string;
  fullPath: string;
  type: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive' | 'custom';
  messages: number;      // 📊 Total de mensajes
  unseen: number;        // 📊 Mensajes no leídos
  hasChildren: boolean;
  selectable: boolean;
  delimiter: string;
}

// 🔧 INTERFAZ para estadísticas
interface FolderStatistics {
  totalFolders: number;
  totalMessages: number;
  totalUnseen: number;
  includeMessageCounts: boolean;
}

// 🔧 INTERFACES PARA EMAILS
interface EmailsCache {
  [folderPath: string]: {
    emails: any[];
    lastUpdate: string;
    totalCount: number;
    page: number;
    hasMore: boolean;
  };
}

interface EmailsState {
  emails: any[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  hasMore: boolean;
  lastUpdate: Date | null;
}

// 🔧 NUEVAS FUNCIONES DE VALIDACIÓN MEJORADAS
const isSystemFolderOrChild = (folderPath: string, allFolders: ServerFolder[]): boolean => {
  // Encontrar la carpeta actual
  const currentFolder = allFolders.find(f => f.fullPath === folderPath);
  
  // Si la carpeta actual es del sistema, retornar true
  if (currentFolder && currentFolder.type !== 'custom') {
    return true;
  }
  
  // Verificar si algún ancestro es carpeta del sistema
  const pathParts = folderPath.split('/');
  
  for (let i = pathParts.length - 1; i > 0; i--) {
    const parentPath = pathParts.slice(0, i).join('/');
    const parentFolder = allFolders.find(f => f.fullPath === parentPath);
    
    if (parentFolder && parentFolder.type !== 'custom') {
      return true;
    }
  }
  
  return false;
};

const getSystemRootFolder = (folderPath: string, allFolders: ServerFolder[]): ServerFolder | null => {
  const pathParts = folderPath.split('/');
  
  for (let i = 1; i <= pathParts.length; i++) {
    const potentialParentPath = pathParts.slice(0, i).join('/');
    const folder = allFolders.find(f => f.fullPath === potentialParentPath);
    
    if (folder && folder.type !== 'custom') {
      return folder;
    }
  }
  
  return null;
};

const isDirectSystemFolder = (folder: ServerFolder): boolean => {
  // Solo las carpetas del sistema principales están protegidas
  return folder.type !== 'custom';
};

// 🔧 NUEVA FUNCIÓN: Verificar si se puede editar/eliminar una carpeta
const canModifyFolder = (folder: ServerFolder): boolean => {
  // Solo las carpetas del sistema DIRECTAS están protegidas
  // Las subcarpetas (incluso de carpetas del sistema) SÍ se pueden eliminar
  return folder.type === 'custom';
};

// 🔧 STORE GLOBAL para Emails con Cache
class EmailsStore {
  private static instance: EmailsStore;
  private cache: EmailsCache = {};
  private currentFolder: string = '';
  private state: EmailsState = {
    emails: [],
    loading: false,
    error: null,
    totalCount: 0,
    currentPage: 1,
    hasMore: true,
    lastUpdate: null
  };
  
  private silentUpdateInterval: NodeJS.Timeout | null = null;
  private subscribers: Set<() => void> = new Set();
  private emailConfig: EmailConfig | null = null;
  private CACHE_DURATION = 10 * 60 * 1000; // 10 minutos
  private SILENT_UPDATE_INTERVAL = 5 * 60 * 1000; // 5 minutos

  static getInstance(): EmailsStore {
    if (!EmailsStore.instance) {
      EmailsStore.instance = new EmailsStore();
    }
    return EmailsStore.instance;
  }

  constructor() {
    this.loadCacheFromLocalStorage();
    this.loadEmailConfig();
    // Cargar la carpeta actual desde localStorage
    const savedFolder = localStorage.getItem('currentEmailFolder');
    if (savedFolder) {
      this.currentFolder = savedFolder;
    }
  }

  // 🔧 SUSCRIPCIÓN Y NOTIFICACIÓN
  subscribe(callback: () => void): () => void {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  private notify() {
    this.subscribers.forEach(callback => callback());
  }

  // 🔧 GESTIÓN DE ESTADO
  private setState(newState: Partial<EmailsState>) {
    this.state = { ...this.state, ...newState };
    this.notify();
  }

  getState() {
    return { ...this.state };
  }

  getCurrentFolder() {
    return this.currentFolder;
  }

  // 🔧 GESTIÓN DE CACHE
  private loadCacheFromLocalStorage() {
    try {
      const stored = localStorage.getItem('emailsCache');
      if (stored) {
        this.cache = JSON.parse(stored);
        console.log('📧 Email cache loaded from localStorage');
      }
    } catch (error) {
      console.error('Error loading email cache:', error);
      this.cache = {};
    }
  }

  private saveCacheToLocalStorage() {
    try {
      localStorage.setItem('emailsCache', JSON.stringify(this.cache));
      console.log('💾 Email cache saved to localStorage');
    } catch (error) {
      console.error('Error saving email cache:', error);
    }
  }

  private loadEmailConfig() {
    try {
      const stored = localStorage.getItem('emailConfig');
      if (stored) {
        this.emailConfig = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading email config:', error);
    }
  }

  private isCacheValid(folderPath: string): boolean {
    const cached = this.cache[folderPath];
    if (!cached) return false;

    const lastUpdate = new Date(cached.lastUpdate);
    const now = new Date();
    const age = now.getTime() - lastUpdate.getTime();

    return age < this.CACHE_DURATION;
  }

  private getCacheKey(folderPath: string): string {
    return folderPath || 'inbox';
  }

  // 🔧 GESTIÓN DE EMAILS
  async loadEmails(folderPath: string = 'inbox', page: number = 1, showLoading: boolean = true) {
    const cacheKey = this.getCacheKey(folderPath);
    this.currentFolder = cacheKey;
    
    // Guardar la carpeta actual en localStorage
    localStorage.setItem('currentEmailFolder', cacheKey);

    // Si es la misma carpeta y página 1, verificar cache
    if (page === 1 && this.isCacheValid(cacheKey)) {
      const cached = this.cache[cacheKey];
      console.log(`📦 Loading emails from cache for ${folderPath}`);
      
      this.setState({
        emails: cached.emails,
        totalCount: cached.totalCount,
        currentPage: cached.page,
        hasMore: cached.hasMore,
        lastUpdate: new Date(cached.lastUpdate),
        loading: false,
        error: null
      });

      // Iniciar actualizaciones silenciosas para esta carpeta
      this.startSilentUpdates(folderPath);
      return;
    }

    // Cargar desde servidor
    await this.fetchEmailsFromServer(folderPath, page, showLoading);
  }

  private async fetchEmailsFromServer(folderPath: string, page: number, showLoading: boolean) {
    if (!this.emailConfig) {
      this.setState({ error: 'Configuración de email no encontrada' });
      return;
    }

    if (showLoading) {
      this.setState({ loading: true, error: null });
    }

    try {
      // 🔧 FIX: Usar offset en lugar de page
      const limit = 50;
      const offset = (page - 1) * limit;

      const response = await fetch('https://backend.anubisai.net/api/pmail/get-emails', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          server: this.emailConfig.server,
          username: this.emailConfig.username,
          password: this.emailConfig.password,
          imapPort: this.emailConfig.imapPort,
          ssl: this.emailConfig.ssl,
          folderPath: folderPath === 'inbox' ? 'INBOX' : folderPath,
          limit,
          offset
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const emails = result.emails || [];
        const totalCount = result.totalCount || emails.length;
        const hasMore = result.hasMore || false;

        // Actualizar estado
        this.setState({
          emails: page === 1 ? emails : [...this.state.emails, ...emails],
          totalCount,
          currentPage: page,
          hasMore,
          lastUpdate: new Date(),
          loading: false,
          error: null
        });

        // Guardar en cache solo si es página 1
        if (page === 1) {
          const cacheKey = this.getCacheKey(folderPath);
          this.cache[cacheKey] = {
            emails,
            lastUpdate: new Date().toISOString(),
            totalCount,
            page,
            hasMore
          };
          this.saveCacheToLocalStorage();
        }

        // Iniciar actualizaciones silenciosas
        this.startSilentUpdates(folderPath);

        console.log(`📧 Loaded ${emails.length} emails from server for ${folderPath}`);
      } else {
        throw new Error(result.error || 'Error al cargar emails');
      }
    } catch (error: any) {
      console.error('Error fetching emails:', error);
      
      // 🔧 FIX: Detener updates silenciosos si hay error
      if (!showLoading) {
        console.log('⏹️ Stopping silent updates due to error');
        this.stopSilentUpdates();
      }
      
      if (showLoading) {
        this.setState({ error: error.message, loading: false });
      }
    }
  }

  // 🔧 ACTUALIZACIONES SILENCIOSAS
  private startSilentUpdates(folderPath: string) {
    this.stopSilentUpdates();
    
    console.log(`⏰ Starting silent email updates for ${folderPath} (every 5 minutes)`);
    this.silentUpdateInterval = setInterval(() => {
      console.log(`🔄 Silent email update triggered for ${folderPath}`);
      this.fetchEmailsFromServer(folderPath, 1, false); // false = silencioso
    }, this.SILENT_UPDATE_INTERVAL);
  }

  private stopSilentUpdates() {
    if (this.silentUpdateInterval) {
      clearInterval(this.silentUpdateInterval);
      this.silentUpdateInterval = null;
      console.log('⏹️ Silent email updates stopped');
    }
  }

  // 🔧 MÉTODOS PÚBLICOS
  async refreshEmails(showLoading: boolean = true) {
    console.log(`🔄 Manual email refresh for ${this.currentFolder}`);
    await this.fetchEmailsFromServer(this.currentFolder, 1, showLoading);
  }

  async loadMoreEmails() {
    if (!this.state.hasMore || this.state.loading) return;
    
    console.log(`📄 Loading more emails (page ${this.state.currentPage + 1})`);
    await this.fetchEmailsFromServer(this.currentFolder, this.state.currentPage + 1, true);
  }

  // Limpiar cache de una carpeta específica
  clearFolderCache(folderPath: string) {
    const cacheKey = this.getCacheKey(folderPath);
    delete this.cache[cacheKey];
    this.saveCacheToLocalStorage();
    console.log(`🗑️ Email cache cleared for ${folderPath}`);
  }

  // Limpiar todo el cache
  clearAllCache() {
    this.cache = {};
    localStorage.removeItem('emailsCache');
    console.log('🗑️ All email cache cleared');
  }

  // Actualizar configuración de email
  setEmailConfig(config: EmailConfig) {
    this.emailConfig = config;
    this.clearAllCache(); // Limpiar cache cuando cambia la configuración
  }

  // Cleanup
  destroy() {
    this.stopSilentUpdates();
    this.subscribers.clear();
    this.saveCacheToLocalStorage();
    console.log('🔄 EmailsStore destroyed');
  }
}

// 🔧 STORE GLOBAL MEJORADO para mantener el estado de carpetas con actualizaciones silenciosas
class FoldersStore {
  private static instance: FoldersStore;
  private folders: ServerFolder[] = [];
  private statistics: FolderStatistics | null = null;
  private loading: boolean = false;
  private error: string | null = null;
  private lastUpdate: Date | null = null;
  private silentUpdateInterval: NodeJS.Timeout | null = null;
  private subscribers: Set<() => void> = new Set();
  private emailConfig: EmailConfig | null = null;
  private isInitialized: boolean = false;
  private SILENT_UPDATE_INTERVAL = 30 * 1000; // 30 segundos para contadores

  static getInstance(): FoldersStore {
    if (!FoldersStore.instance) {
      FoldersStore.instance = new FoldersStore();
    }
    return FoldersStore.instance;
  }

  subscribe(callback: () => void): () => void {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  private notify() {
    this.subscribers.forEach(callback => callback());
  }

  setFolders(folders: ServerFolder[], statistics?: FolderStatistics) {
    this.folders = folders;
    this.statistics = statistics || null;
    this.lastUpdate = new Date();
    this.notify();
  }

  getStatistics() { 
    return this.statistics; 
  }

  setLoading(loading: boolean) {
    this.loading = loading;
    this.notify();
  }

  setError(error: string | null) {
    this.error = error;
    this.notify();
  }

  setEmailConfig(config: EmailConfig | null) {
    this.emailConfig = config;
    if (config) {
      this.startSilentUpdates();
    } else {
      this.stopSilentUpdates();
    }
  }

  getFolders() { return this.folders; }
  getLoading() { return this.loading; }
  getError() { return this.error; }
  getLastUpdate() { return this.lastUpdate; }
  getEmailConfig() { return this.emailConfig; }
  getIsInitialized() { return this.isInitialized; }

  async fetchFolders(showLoading = true) {
    if (!this.emailConfig) return;

    // Solo mostrar loading en el refresh manual, no en actualizaciones silenciosas
    if (showLoading) {
      this.setLoading(true);
      this.setError(null);
    }

    try {
      const response = await fetch('https://backend.anubisai.net/api/pmail/get-folders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          server: this.emailConfig.server,
          username: this.emailConfig.username,
          password: this.emailConfig.password,
          imapPort: this.emailConfig.imapPort,
          ssl: this.emailConfig.ssl,
          includeMessageCounts: true // 📊 Incluir conteos
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Guardar las carpetas anteriores para comparar
        const previousFolders = [...this.folders];
        
        this.setFolders(result.folders, result.statistics);
        this.setError(null);
        this.isInitialized = true;
        
        // Log diferente para actualizaciones silenciosas
        if (!showLoading) {
          // Detectar cambios en los contadores
          let changesDetected = false;
          result.folders.forEach((folder: ServerFolder) => {
            const prevFolder = previousFolders.find(f => f.fullPath === folder.fullPath);
            if (prevFolder && (prevFolder.unseen !== folder.unseen || prevFolder.messages !== folder.messages)) {
              changesDetected = true;
            }
          });
          
          if (changesDetected) {
            console.log('📁 Folders silently updated - counter changes detected');
          }
        } else {
          console.log('📁 Folders loaded with message counts:', result.statistics);
        }
      } else {
        if (showLoading) {
          this.setError(result.error || 'Error al obtener carpetas');
        }
        // No limpiar carpetas en error silencioso
        if (!showLoading && this.folders.length > 0) {
          console.log('📁 Silent update failed, keeping current folders');
        } else {
          this.setFolders([]);
        }
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
      if (showLoading) {
        this.setError('Error de conexión al obtener carpetas');
        this.setFolders([]);
      } else {
        // En actualizaciones silenciosas, mantener las carpetas actuales si hay error
        console.log('📁 Silent update error, keeping current folders');
      }
    } finally {
      if (showLoading) {
        this.setLoading(false);
      }
    }
  }

  private startSilentUpdates() {
    this.stopSilentUpdates();
    
    // Actualización silenciosa cada 30 segundos para contadores
    this.silentUpdateInterval = setInterval(() => {
      console.log('🔄 Silent folder counter update triggered');
      this.fetchFolders(false); // false = actualización silenciosa
    }, this.SILENT_UPDATE_INTERVAL);
    
    console.log('⏰ Silent counter updates started (every 30 seconds)');
  }

  private stopSilentUpdates() {
    if (this.silentUpdateInterval) {
      clearInterval(this.silentUpdateInterval);
      this.silentUpdateInterval = null;
      console.log('⏹️ Silent counter updates stopped');
    }
  }

  async saveConfig(config: EmailConfig) {
    try {
      const response = await fetch('https://backend.anubisai.net/api/pmail/save-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        localStorage.setItem('emailConfig', JSON.stringify(config));
        this.setEmailConfig(config);
        await this.fetchFolders(true); // true = mostrar loading
        return { success: true };
      } else {
        throw new Error(result.error || 'Error al guardar configuración');
      }
    } catch (error) {
      console.error('Error guardando configuración:', error);
      return { success: false, error: error.message };
    }
  }

  initialize() {
    const savedConfig = localStorage.getItem('emailConfig');
    if (savedConfig) {
      try {
        const config = JSON.parse(savedConfig);
        this.setEmailConfig(config);
        
        // Primera carga con loading, después silenciosa
        if (!this.isInitialized) {
          this.fetchFolders(true); // true = mostrar loading inicial
        }
      } catch (error) {
        console.error('Error loading saved config:', error);
        this.initializeWithDefaults();
      }
    } else {
      this.initializeWithDefaults();
    }
  }

  private initializeWithDefaults() {
    console.log('📧 Initializing with default email config');
    localStorage.setItem('emailConfig', JSON.stringify(DEFAULT_EMAIL_CONFIG));
    this.setEmailConfig(DEFAULT_EMAIL_CONFIG);
    this.fetchFolders(true); // true = mostrar loading inicial
  }

  // 🔧 NUEVA FUNCIÓN: Refresh manual (con loading)
  async refreshFolders() {
    console.log('🔄 Manual folder refresh triggered');
    await this.fetchFolders(true); // true = mostrar loading
  }

  destroy() {
    this.stopSilentUpdates();
    this.subscribers.clear();
    this.isInitialized = false;
  }
}

// 🔧 CUSTOM HOOK para usar el store de emails
const useEmailsStore = () => {
  const store = EmailsStore.getInstance();
  const [, forceUpdate] = useState({});

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      forceUpdate({});
    });

    return unsubscribe;
  }, [store]);

  return {
    ...store.getState(),
    loadEmails: (folderPath?: string, page?: number, showLoading?: boolean) => 
      store.loadEmails(folderPath, page, showLoading),
    refreshEmails: (showLoading?: boolean) => 
      store.refreshEmails(showLoading),
    loadMoreEmails: () => 
      store.loadMoreEmails(),
    clearFolderCache: (folderPath: string) => 
      store.clearFolderCache(folderPath),
    clearAllCache: () => 
      store.clearAllCache(),
    setEmailConfig: (config: EmailConfig) => 
      store.setEmailConfig(config)
  };
};

// 🔧 CUSTOM HOOK para usar el store
const useFoldersStore = () => {
  const store = FoldersStore.getInstance();
  const [, forceUpdate] = useState({});

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      forceUpdate({});
    });

    return unsubscribe;
  }, [store]);

  return {
    folders: store.getFolders(),
    statistics: store.getStatistics(), // 📊 Nuevo campo
    loading: store.getLoading(),
    error: store.getError(),
    emailConfig: store.getEmailConfig(),
    lastUpdate: store.getLastUpdate(),
    isInitialized: store.getIsInitialized(),
    refreshFolders: () => store.refreshFolders(),
    saveConfig: (config: EmailConfig) => store.saveConfig(config),
    initialize: () => store.initialize()
  };
};

// 🔧 COMPONENTE MEJORADO: Modal Simple para Crear Carpeta con Color
interface CreateFolderModalProps {
  show: boolean;
  onHide: () => void;
  onCreated: () => void;
  parentFolder?: string;
}

const CreateFolderModal: React.FC<CreateFolderModalProps> = ({ 
  show, 
  onHide, 
  onCreated, 
  parentFolder = ''
}) => {
  const [folderName, setFolderName] = useState('');
  const [selectedColor, setSelectedColor] = useState('#3b82f6'); // Azul por defecto
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 🎨 Colores predefinidos para las carpetas
  const folderColors = [
    { name: 'Azul', value: '#3b82f6' },
    { name: 'Verde', value: '#10b981' },
    { name: 'Rojo', value: '#ef4444' },
    { name: 'Amarillo', value: '#f59e0b' },
    { name: 'Púrpura', value: '#8b5cf6' },
    { name: 'Rosa', value: '#ec4899' },
    { name: 'Naranja', value: '#f97316' },
    { name: 'Turquesa', value: '#06b6d4' },
    { name: 'Gris', value: '#6b7280' },
    { name: 'Índigo', value: '#6366f1' },
    { name: 'Verde lima', value: '#84cc16' },
    { name: 'Esmeralda', value: '#059669' }
  ];

  const handleCreate = async () => {
    const validation = validateFolderName(folderName);
    if (!validation.valid) {
      setError(validation.error!);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await createFolder(folderName.trim(), parentFolder);
      
      if (result.success) {
        // TODO: Guardar el color en localStorage o base de datos
        const folderColors = JSON.parse(localStorage.getItem('folderColors') || '{}');
        folderColors[result.folderPath || folderName] = selectedColor;
        localStorage.setItem('folderColors', JSON.stringify(folderColors));
        
        alert(`✅ ${result.message}`);
        resetForm();
        onHide();
        onCreated();
      } else {
        setError(result.message);
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFolderName('');
    setSelectedColor('#3b82f6');
    setError(null);
  };

  // Reset form cuando se cierra el modal
  useEffect(() => {
    if (!show) {
      resetForm();
    }
  }, [show]);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <FontAwesomeIcon icon={faPlus} className="me-2" style={{ color: selectedColor }} />
          Nueva Carpeta
          {parentFolder && (
            <small className="text-muted ms-2">en "{parentFolder}"</small>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {/* 📝 Nombre de la carpeta */}
          <Form.Group className="mb-4">
            <Form.Label className="fw-semibold">Nombre de la carpeta</Form.Label>
            <Form.Control
              type="text"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="Ingrese el nombre de la carpeta"
              disabled={loading}
              autoFocus
              className="form-control-lg"
            />
            {error && (
              <div className="text-danger small mt-1">{error}</div>
            )}
          </Form.Group>
          
          {/* 🎨 Selector de color */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Color de identificación</Form.Label>
            <div className="d-flex flex-wrap gap-2 mt-2">
              {folderColors.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  className={`btn btn-outline-secondary p-2 d-flex align-items-center justify-content-center ${
                    selectedColor === color.value ? 'active' : ''
                  }`}
                  onClick={() => setSelectedColor(color.value)}
                  style={{
                    width: '50px',
                    height: '50px',
                    backgroundColor: selectedColor === color.value ? color.value : 'transparent',
                    borderColor: color.value,
                    borderWidth: '2px'
                  }}
                  title={color.name}
                  disabled={loading}
                >
                  <FontAwesomeIcon 
                    icon={faFolder} 
                    style={{ 
                      color: selectedColor === color.value ? 'white' : color.value,
                      fontSize: '18px'
                    }} 
                  />
                </button>
              ))}
            </div>
            <small className="text-muted">
              Selecciona un color para identificar fácilmente tu carpeta
            </small>
          </Form.Group>

          {/* 📋 Preview de la carpeta */}
          <div className="border rounded p-3 bg-light">
            <small className="text-muted d-block mb-2">Vista previa:</small>
            <div className="d-flex align-items-center gap-2">
              <FontAwesomeIcon 
                icon={faFolder} 
                style={{ color: selectedColor, fontSize: '16px' }} 
              />
              <span className="fw-semibold">
                {folderName || 'Nombre de la carpeta'}
              </span>
              <span className="badge bg-secondary">0</span>
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancelar
        </Button>
        <Button 
          variant="primary" 
          onClick={handleCreate}
          disabled={loading || !folderName.trim()}
          className="d-flex align-items-center"
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Creando...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Crear Carpeta
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// 🔧 COMPONENTE MEJORADO: Item de Carpeta con validación corregida
interface FolderItemProps {
  folder: ServerFolder;
  allFolders: ServerFolder[];
  level?: number;
  onFolderAction: (action: string, folder: ServerFolder) => void;
}

const FolderItem: React.FC<FolderItemProps> = ({ 
  folder, 
  allFolders, 
  level = 0, 
  onFolderAction 
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const { folderPath } = useParams<{ folderPath?: string }>();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const isActive = () => {
    if (location.pathname === '/anubis/email/inbox' && folder.type === 'inbox') {
      return true;
    }
    if (folderPath && decodeURIComponent(folderPath) === folder.fullPath) {
      return true;
    }
    return false;
  };

  const getFolderIcon = (type: string) => {
    switch (type) {
      case 'inbox': return <FontAwesomeIcon icon={faInbox} size="sm" />;
      case 'sent': return <FontAwesomeIcon icon={faPaperPlane} size="sm" />;
      case 'drafts': return <FontAwesomeIcon icon={faEdit} size="sm" />;
      case 'trash': return <FontAwesomeIcon icon={faTrash} size="sm" />;
      case 'spam': return <FontAwesomeIcon icon={faExclamationTriangle} size="sm" />;
      case 'archive': return <FontAwesomeIcon icon={faArchive} size="sm" />;
      default: return <FontAwesomeIcon icon={faFolder} size="sm" />;
    }
  };

  // 🔧 FUNCIÓN: Obtener color del badge según el tipo de carpeta
  const getBadgeClass = (type: string, unseen: number) => {
    if (unseen === 0) return 'nav-item-count-read'; // Gris para leídos
    
    switch (type) {
      case 'inbox': return 'nav-item-count-inbox'; // Azul para inbox
      case 'spam': return 'nav-item-count-spam';   // Rojo para spam
      default: return 'nav-item-count';            // Verde para otros
    }
  };

  const subfolders = allFolders.filter(f => {
    if (f.fullPath === folder.fullPath) return false;
    if (!f.fullPath.startsWith(folder.fullPath + folder.delimiter)) return false;
    const relativePath = f.fullPath.substring(folder.fullPath.length + folder.delimiter.length);
    return !relativePath.includes(folder.delimiter);
  });

  // 🔧 VALIDACIÓN: Solo verificar si es carpeta del sistema DIRECTA
  const isSystemFolder = isDirectSystemFolder(folder);
  const canEdit = canModifyFolder(folder);
  const canDelete = canModifyFolder(folder);
  
  // 🔧 Manejar click del menú contextual
  const handleMenuAction = (action: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDropdown(false);
    
    if ((action === 'rename' || action === 'delete') && isSystemFolder) {
      alert(`❌ No se pueden ${action === 'rename' ? 'renombrar' : 'eliminar'} carpetas del sistema como "${folder.name}"`);
      return;
    }
    
    onFolderAction(action, folder);
  };

  const toggleDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  return (
    <>
      <div style={{ paddingLeft: `${level * 20}px` }}>
        <Nav.Item className="position-relative">
          <div className="d-flex align-items-center">
            {subfolders.length > 0 && (
              <Button
                variant="link"
                size="sm"
                className="p-0 me-1 text-body-tertiary"
                onClick={() => setIsExpanded(!isExpanded)}
                style={{ width: '16px' }}
              >
                <FontAwesomeIcon 
                  icon={isExpanded ? faCaretDown : faCaretRight} 
                  className="fs-10"
                />
              </Button>
            )}
            
            <Nav.Link
              as={Link}
              className={classNames(
                'py-2 ps-0 pe-3 border-end border-bottom border-translucent text-start outline-none flex-1 d-flex align-items-center justify-content-between',
                { active: isActive() }
              )}
              to={folder.type === 'inbox' 
                ? '/anubis/email/inbox'
                : `/anubis/email/folder/${encodeURIComponent(folder.fullPath)}`}
              style={{ marginLeft: subfolders.length === 0 ? '20px' : '0' }}
            >
              <div className="d-flex gap-2 align-items-center">
                {getFolderIcon(folder.type)}
                <span className="flex-1">{folder.name}</span>
                
                {/* 📊 MOSTRAR CONTADORES */}
                <div className="d-flex gap-1 align-items-center">
                  {/* Contador de no leídos (prominente) */}
                  {folder.unseen > 0 && (
                    <span 
                      className={`badge  text-body ${getBadgeClass(folder.type, folder.unseen)}`}
                      style={{
                        background: 'linear-gradient(transparent,rgba(56, 116, 255, 0.61))',
                        //opacity: 0.1,
                        //backgroundColor: 'linear-gradient(transparent,rgba(56, 116, 255, 0.61))',
                      }}
                      title={`${folder.unseen} mensaje${folder.unseen !== 1 ? 's' : ''} no leído${folder.unseen !== 1 ? 's' : ''}`}
                    >
                      {folder.unseen}
                    </span>
                  )}
                  
                  {/* Contador total (discreto) */}
                  {folder.messages > 0 && (
                    <span 
                      className="badge badge-subtle text-muted fs-10"
                      title={`${folder.messages} mensaje${folder.messages !== 1 ? 's' : ''} total${folder.messages !== 1 ? 'es' : ''}`}
                      style={{ 
                        fontSize: '0.7rem',
                        opacity: 0.7,
                        fontWeight: 'normal'
                      }}
                    >
                      {folder.messages > 999 ? '999+' : folder.messages}
                    </span>
                  )}
                </div>
              </div>
            </Nav.Link>

            {/* 🔧 MENÚ CONTEXTUAL */}
            <div 
              ref={dropdownRef}
              className="position-absolute end-0 top-50 translate-middle-y me-3" 
              style={{ zIndex: 1050 }}
            >
              <Button
                variant="link"
                size="sm"
                className="p-1 text-body-tertiary border-0"
                style={{ boxShadow: 'none' }}
                onClick={toggleDropdown}
                title="Opciones de carpeta"
              >
                <FontAwesomeIcon icon={faEllipsisV} className="fs-10" />
              </Button>

              {showDropdown && (
                <div 
                  className="dropdown-menu show position-absolute"
                  style={{ 
                    top: '100%', 
                    right: '0',
                    zIndex: 1060,
                    minWidth: '180px',
                    boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
                  }}
                >
                  {/* Crear subcarpeta */}
                  <button 
                    className="dropdown-item d-flex align-items-center"
                    onClick={(e) => handleMenuAction('create-sub', e)}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Crear subcarpeta
                  </button>
                  
                  {/* 📊 INFORMACIÓN DE CONTADORES */}
                  {folder.messages > 0 && (
                    <>
                      <div className="dropdown-divider"></div>
                      <div className="dropdown-item-text">
                        <small className="text-muted">
                          📊 {folder.messages} mensaje{folder.messages !== 1 ? 's' : ''} total
                          {folder.unseen > 0 && (
                            <><br />📬 {folder.unseen} no leído{folder.unseen !== 1 ? 's' : ''}</>
                          )}
                        </small>
                      </div>
                    </>
                  )}
                  
                  {/* Opciones de edición */}
                  {canEdit && (
                    <>
                      <div className="dropdown-divider"></div>
                      <button 
                        className="dropdown-item d-flex align-items-center"
                        onClick={(e) => handleMenuAction('rename', e)}
                      >
                        <FontAwesomeIcon icon={faEdit} className="me-2" />
                        Renombrar
                      </button>
                    </>
                  )}
                  
                  {canDelete && (
                    <>
                      <div className="dropdown-divider"></div>
                      <button 
                        className="dropdown-item d-flex align-items-center text-danger"
                        onClick={(e) => handleMenuAction('delete', e)}
                      >
                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                        Eliminar
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </Nav.Item>
      </div>
      
      {isExpanded && subfolders.map(subfolder => (
        <FolderItem 
          key={subfolder.fullPath}
          folder={subfolder}
          allFolders={allFolders}
          level={level + 1}
          onFolderAction={onFolderAction}
        />
      ))}
    </>
  );
};

// 🔧 COMPONENTE PRINCIPAL: EmailSidebar con Store
const EmailSidebar = ({ hideSidebar }: { hideSidebar?: () => void }) => {
  const {
    folders: serverFolders,
    statistics, // 📊 Nuevo campo
    loading: loadingFolders,
    error: foldersError,
    emailConfig,
    refreshFolders,
    saveConfig,
    initialize
  } = useFoldersStore();

  // Estados para modales
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<ServerFolder | null>(null);
  const [actionType, setActionType] = useState<'rename' | 'delete' | 'create-sub' | null>(null);
  const [loading, setLoading] = useState(false);

  // 🔧 Inicializar el store solo una vez
  useEffect(() => {
    console.log('🚀 EmailSidebar mounted, initializing store...');
    initialize();
    
    if (typeof window !== 'undefined') {
      window.getEmailsStore = () => EmailsStore.getInstance();
    }
    
    return () => {
      console.log('🔄 EmailSidebar unmounted');
    };
  }, []);

  // 🔧 FUNCIÓN: Manejar acciones de carpetas
  const handleFolderAction = async (action: string, folder: ServerFolder) => {
    console.log(`📁 Folder action: ${action} on ${folder.name}`);
    
    switch (action) {
      case 'rename':
        if (isDirectSystemFolder(folder)) {
          alert(`❌ No se pueden renombrar carpetas del sistema como "${folder.name}"`);
          return;
        }
        
        const newName = prompt(`Renombrar carpeta "${folder.name}":`, folder.name);
        if (newName && newName.trim() !== folder.name) {
          const validation = validateFolderName(newName);
          if (!validation.valid) {
            alert(`❌ ${validation.error}`);
            return;
          }
          
          try {
            const result = await renameFolder(folder.fullPath, newName.trim());
            if (result.success) {
              alert(`✅ ${result.message}`);
              refreshFolders();
            } else {
              alert(`❌ ${result.message}`);
            }
          } catch (error: any) {
            alert(`❌ Error: ${error.message}`);
          }
        }
        break;

      case 'create-sub':
        setSelectedFolder(folder);
        setActionType('create-sub');
        setShowCreateModal(true);
        break;

      case 'delete':
        if (isDirectSystemFolder(folder)) {
          alert(`❌ No se pueden eliminar carpetas del sistema como "${folder.name}"`);
          return;
        }
        
        const confirmMessage = `¿Estás seguro de que quieres eliminar la carpeta "${folder.name}"?\n\n` +
          `⚠️ Esta acción eliminará la carpeta y todo su contenido de forma permanente.`;
        
        if (confirm(confirmMessage)) {
          try {
            const result = await deleteFolder(folder.fullPath);
            if (result.success) {
              alert(`✅ ${result.message}`);
              refreshFolders();
              
              const emailsStore = EmailsStore.getInstance();
              emailsStore.clearFolderCache(folder.fullPath);
            } else {
              alert(`❌ ${result.message}`);
            }
          } catch (error: any) {
            alert(`❌ Error: ${error.message}`);
          }
        }
        break;
    }
  };

  // 🔧 Función para guardar configuración
  const handleSaveEmailConfig = async (config: EmailConfig) => {
    setLoading(true);
    
    try {
      const result = await saveConfig(config);
      
      if (result.success) {
        const emailsStore = EmailsStore.getInstance();
        emailsStore.setEmailConfig(config);
        
        setShowSettingsModal(false);
        alert('Configuración de email guardada correctamente');
      } else {
        throw new Error(result.error || 'Error al guardar configuración');
      }
      
    } catch (error: any) {
      console.error('Error guardando configuración:', error);
      alert('Error al guardar la configuración de email');
    } finally {
      setLoading(false);
    }
  };

  // Obtener carpetas de nivel superior ordenadas
  const topLevelFolders = serverFolders.filter(folder => {
    const parts = folder.fullPath.split(folder.delimiter || '/');
    return parts.length === 1 || (parts.length === 2 && parts[0] === '');
  }).sort((a, b) => {
    if (a.name.toLowerCase() === 'inbox') return -1;
    if (b.name.toLowerCase() === 'inbox') return 1;
    return a.name.localeCompare(b.name);
  });

  const hasEmailConfig = !!emailConfig;

  return (
    <div>
      <div className="email-content scrollbar">
        {/* 🔧 HEADER CON ESTADÍSTICAS */}
        <div className="d-flex flex-between-center mb-2">
          <div className="d-flex flex-column">
            <p className="text-uppercase fs-10 text-body-tertiary text-opacity-85 mb-0 fw-bold">
              Mailbox
            </p>
            {/* 📊 MOSTRAR ESTADÍSTICAS SI ESTÁN DISPONIBLES */}
            {statistics && (
              <small className="text-muted fs-11">
                {statistics.totalMessages > 0 ? (
                  <>
                    {statistics.totalMessages} mensaje{statistics.totalMessages !== 1 ? 's' : ''}
                    {statistics.totalUnseen > 0 && (
                      <span className="text-primary fw-semibold">
                        {' '}• {statistics.totalUnseen} no leído{statistics.totalUnseen !== 1 ? 's' : ''}
                      </span>
                    )}
                  </>
                ) : (
                  'Sin mensajes'
                )}
              </small>
            )}
          </div>
          
          <div className="d-flex gap-1">
            {/* Icono Crear Carpeta */}
            <Button
              variant="link"
              size="sm"
              className="p-1 text-body-tertiary"
              onClick={() => setShowCreateModal(true)}
              title="Nueva carpeta"
            >
              <FontAwesomeIcon icon={faPlus} className="fs-8" />
            </Button>
            
            {/* Icono Actualizar */}
            <Button
              variant="link"
              size="sm"
              className="p-1 text-body-tertiary"
              onClick={refreshFolders}
              disabled={loadingFolders}
              title="Actualizar carpetas y contadores"
            >
              {loadingFolders ? (
                <Spinner animation="border" size="sm" style={{ width: '14px', height: '14px' }} />
              ) : (
                <FontAwesomeIcon icon={faSync} className="fs-8" />
              )}
            </Button>
            
            {/* Botón cerrar sidebar (móvil) */}
            {hideSidebar && (
              <Button
                className="d-lg-none p-1"
                onClick={() => hideSidebar()}
              >
                <UilTimes size={14} />
              </Button>
            )}
          </div>
        </div>

        {/* 📊 PANEL DE ESTADÍSTICAS EXPANDIDO (opcional) */}
        {statistics && statistics.totalMessages > 0 && (
          <div className="bg-body rounded p-2 mb-3">
            <div className="row g-2 text-center">
              <div className="col-4">
                <div className="fs-7 fw-bold text-primary">{statistics.totalFolders}</div>
                <div className="fs-11 text-muted">Carpetas</div>
              </div>
              <div className="col-4">
                <div className="fs-7 fw-bold text-success">{statistics.totalMessages}</div>
                <div className="fs-11 text-muted">Total</div>
              </div>
              <div className="col-4">
                <div className="fs-7 fw-bold text-warning">{statistics.totalUnseen}</div>
                <div className="fs-11 text-muted">No leídos</div>
              </div>
            </div>
          </div>
        )}

        {/* Carpetas del servidor */}
        {hasEmailConfig && (
          <>
            {loadingFolders ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
                <p className="text-muted fs-9 mt-2 mb-0">Cargando carpetas y contadores...</p>
              </div>
            ) : foldersError ? (
              <div className="alert alert-warning py-2 mb-3">
                <small>{foldersError}</small>
                <br />
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 mt-1"
                  onClick={refreshFolders}
                >
                  Reintentar
                </Button>
              </div>
            ) : topLevelFolders.length > 0 ? (
              <Nav className="flex-column border-top border-translucent fs-9 vertical-nav mb-4">
                {topLevelFolders.map(folder => (
                  <FolderItem 
                    key={folder.fullPath}
                    folder={folder}
                    allFolders={serverFolders}
                    onFolderAction={handleFolderAction}
                  />
                ))}
              </Nav>
            ) : (
              <div className="text-center py-3">
                <p className="text-muted fs-9 mb-2">No se encontraron carpetas</p>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={refreshFolders}
                >
                  <FontAwesomeIcon icon={faSync} className="me-1" />
                  Actualizar
                </Button>
              </div>
            )}
          </>
        )}

        {/* Carpetas filtradas (fallback para usuarios sin config) */}
        {!hasEmailConfig && (
          <>
            <div className="d-flex flex-between-center mb-2">
              <p className="text-uppercase fs-10 text-body-tertiary text-opacity-85 mb-0 fw-bold">
                Filtered
              </p>
              <Button
                variant="link"
                className="fs-10 fw-bold p-0"
                startIcon={<FontAwesomeIcon icon={faPlus} className="me-2" />}
              >
                Add Folder
              </Button>
            </div>
            <Nav className="flex-column border-top border-translucent fs-9 vertical-nav mb-4">
              {filteredItems.map(item => (
                <EmailSidebarItem item={item} key={item.label} />
              ))}
            </Nav>
          </>
        )}

        {/* Labels */}
        <div className="d-flex flex-between-center mb-2">
          <p className="text-uppercase fs-10 text-body-tertiary text-opacity-85 mb-0 fw-bold">
            Labels
          </p>
          <Button
            variant="link"
            className="fs-10 fw-bold p-0"
            startIcon={<FontAwesomeIcon icon={faPlus} className="me-2" />}
          >
            Add Label
          </Button>
        </div>

        {/*<Nav className="flex-column border-top border-translucent fs-9 vertical-nav">
          {labelItems.map(item => (
            <EmailSidebarItem item={item} key={item.label} />
          ))}
        </Nav>*/}

        {/* Botón de configuración */}
        <EmailSettingsButton 
          onClick={() => setShowSettingsModal(true)}
          hasConfig={hasEmailConfig}
        />

        {/* Estado de conexión con estadísticas */}
        {hasEmailConfig && emailConfig && (
          <div className="border-top border-translucent pt-2 mt-2">
            <small className="text-success d-block text-center">
              <FontAwesomeIcon icon={faInbox} className="me-1" />
              Conectado a {emailConfig.server}
            </small>
            {statistics && (
              <small className="text-muted d-block text-center mt-1">
                Última actualización: {new Date().toLocaleTimeString('es-CL', { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </small>
            )}
          </div>
        )}
      </div>

      {/* Modal: Crear carpeta */}
      <CreateFolderModal
        show={showCreateModal}
        onHide={() => {
          setShowCreateModal(false);
          setSelectedFolder(null);
          setActionType(null);
        }}
        onCreated={refreshFolders}
        parentFolder={selectedFolder?.fullPath || ''}
      />

      {/* Modal de configuración */}
      <EmailSettingsModal
        show={showSettingsModal}
        onHide={() => setShowSettingsModal(false)}
        onSave={handleSaveEmailConfig}
        loading={loading}
      />
    </div>
  );
};

// Componente para items del sidebar tradicional
const EmailSidebarItem = ({ item }: { item: SidebarItem }) => {
  return (
    <Nav.Item>
      <Nav.Link
        as={Link}
        className={classNames(
          'py-2 ps-0 pe-3 border-end border-bottom border-translucent text-start outline-none',
          { active: item.active }
        )}
        to={item.link ? item.link : '#!'}
      >
        <div className="d-flex gap-2 align-items-center">
          {item.icon}
          <span className="flex-1">{item.label}</span>
          {item.count && <span className="nav-item-count">{item.count}</span>}
        </div>
      </Nav.Link>
    </Nav.Item>
  );
};

// 🔧 FUNCIÓN GLOBAL PARA ACCEDER AL EMAILSSTORE
declare global {
  interface Window {
    getEmailsStore: () => any;
  }
}

export default EmailSidebar;